/**
 * Validation for the Issue Certificate form (pages/Certificates, F12).
 *
 * Before this, a blank Principals field silently issued an SSH certificate for
 * `admin`, a blank Common Name silently issued TLS for `localhost`, and validity
 * accepted 24h for SSH while the hint said the SSH maximum is 8h. Each of those is
 * now a field error, and the limits are the ones the hint states.
 */

export type CertType = 'ssh' | 'tls';

/** Hours. The form's hint and its validation both read from here. */
export const MAX_VALIDITY_HOURS: Record<CertType, number> = { ssh: 8, tls: 24 };

export interface IssueFormValues {
    principals: string;
    commonName: string;
    validity: string;
}

export type IssueRequest =
    | { type: 'ssh'; body: { principals: string[]; validity_hours: number } }
    | { type: 'tls'; body: { dns_names: string[]; subject: { common_name: string }; validity_hours: number } };

export interface IssueValidation {
    request?: IssueRequest;
    errors: Partial<Record<keyof IssueFormValues, string>>;
}

const splitList = (value: string) => value.split(',').map(v => v.trim()).filter(Boolean);

export function validateIssueForm(type: CertType, values: IssueFormValues): IssueValidation {
    const errors: IssueValidation['errors'] = {};

    const max = MAX_VALIDITY_HOURS[type];
    const raw = values.validity.trim();
    const hours = Number(raw);
    if (!raw || !Number.isInteger(hours) || hours < 1 || hours > max) {
        errors.validity = `Enter a whole number of hours from 1 to ${max}.`;
    }

    if (type === 'ssh') {
        const principals = splitList(values.principals);
        if (principals.length === 0) errors.principals = 'Enter at least one principal (the login name the certificate is valid for).';
        if (Object.keys(errors).length) return { errors };
        return { errors, request: { type: 'ssh', body: { principals, validity_hours: hours } } };
    }

    const names = splitList(values.commonName);
    if (names.length === 0) errors.commonName = 'Enter a common name (for example api.company.com).';
    if (Object.keys(errors).length) return { errors };
    return {
        errors,
        request: { type: 'tls', body: { dns_names: names, subject: { common_name: names[0] }, validity_hours: hours } },
    };
}
